import React, { useEffect, useState } from "react";
import "./styles/PhishingManager.module.scss";
import CampaignSetup from "./CampaignSetup";
import { Alert, Dropdown, Menu, Modal, Pagination, Select, Spin } from "antd";
import { Icon } from "Components";
import { GetGroupsListRequest, GetGroupsListResponse } from "Services/Api/Groups/Types";
import { DEFAULT_TABLE_REQUEST_VALUES } from "Pages/Groups/Group/Group";
import { useLazyQuery } from "@apollo/client";
import { GET_GROUPS_LIST } from "Services/Api/Groups/Queries";
import { authStore } from "Stores/AuthStore";
import { SystemRoleType } from "Services/Api/Roles/Types";
import { getPhishingCampaignData, getPhishingCampaignsForTenant } from "Services/Api/PhishingManager/PhishingManager.Service";
import moment from "moment";

interface CampaignData {
  id: number | string;
  name: string;
  status: string;
  campaign_type: string;
  schedule_datetime: string | null;
  schedule_timezone: string | null;
  campaign_duration: number;
  image_url: string;
}

interface CampaignThresholdData {
  campaign_count: number;
  first_campaign_created_at: string;
  tenant_campaigns_threshold: number;
  current_period_started_at: string;
  threshold_period_months: number;
  is_threshold_reached: boolean;
}

const PhishingManager: React.FC = () => {


  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [campaignThresholdData, setCampaignThresholdData] = useState<CampaignThresholdData | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedTenantId, setSelectedTenantId] = useState<string | number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [phishingArray, setPhishingArray] = useState<CampaignData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [itemToDiscard, setItemToDiscard] = useState<string | null>(null);
  const [warningModal, setWarningModal] = useState(false);
  const [newCampaignSetup, setNewCampaignSetup] = useState(false);
  const statusStyles: Record<string, { background: string; color: string }> = {
    Scheduled: { background: "#EAEAEB", color: "#000" },
    Live: { background: "#F0EFFD", color: "#6B5AED" },
    Testing: { background: "#FEF6E9", color: "#000" },
    Draft: { background: "#FEF6E9", color: "#000" },
    Complete: { background: "#CDE7CF", color: "#2E7D32" },
    Cancelled: { background: "#FBEAEA", color: "#D32F2F" },
    Failed: { background: "#FBEAEA", color: "#D32F2F" },
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const threshold = campaignThresholdData?.tenant_campaigns_threshold ?? 0;

  const handleMenuClick = (itemId: string) => (info: any) => {
    const menuKey = info.key.toString();

    if (menuKey === 'discard') {
      setItemToDiscard(itemId);
      setIsModalVisible(true);
    } else {
      console.log(`${menuKey} action for:`, itemId);
    }
  };

  const [getGroup, result] = useLazyQuery<
    GetGroupsListResponse,
    GetGroupsListRequest
  >(GET_GROUPS_LIST, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const TENANTS: { label: string; value: string }[] =
    result?.data?.listGroups?.map(el => ({
      label: el.name,
      value: String(el.id),
    })) ?? [];

  const fetchTenants = (
    values: GetGroupsListRequest = DEFAULT_TABLE_REQUEST_VALUES
  ) => {
    setIsLoading(true);
    const variables: GetGroupsListRequest = {
      sort: values.sort,
      filters: {
        ...values.filters,
        depth: 1,
        parentId: null
      }
    };

    setIsLoading(false);
    if (result.refetch) {
      result.refetch(variables);
    } else {
      getGroup({
        variables
      });
    }
  };

  const confirmDiscard = () => {
    if (itemToDiscard) {
      setPhishingArray(prev => prev.filter(el => el.id !== Number(itemToDiscard)));
    }
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const confirmWarningDiscard = () => {
    newCampaign();
    setWarningModal(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const closeWarningModal = () => {
    setWarningModal(false);
  };

  const handleCreateCampaign = () => {
    if (phishingArray.length === (threshold - 1)) {
      setWarningModal(true);
    } else if (!!campaignThresholdData?.is_threshold_reached) {
      setErrorMessage("Campaign limit reached. No more campaigns can be created.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    } else {
      if (!!campaignThresholdData) {
        newCampaign();
      } else {
        if (!!errorMessage) {
          setErrorMessage(errorMessage);
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
          }, 3000)
        }
      }
    }
  };

  const newCampaign = () => {
    setNewCampaignSetup(true);
  }
  const isSuperAdmin =
    authStore.currentUser?.primarySystemRole?.role ===
    SystemRoleType.SuperAdmin;


  const handleTenantChange = async (id: string | number) => {
    setIsLoading(true);
    setCurrentPage(1);
    await fetchCampaigns(id);
    await fetchCampaignsData(id);
    setSelectedTenantId(id);
  }

  const fetchCampaigns = async (value: any, pagination?: any) => {
    try {
      setIsLoading(true);
      const page = pagination ?? 1
      const response = await getPhishingCampaignsForTenant(value, page);
      if (response?.meta) {
        setTotalItems(response.meta.total);
        setPageSize(response.meta.per_page);
        setCurrentPage(response.meta.current_page);
      }
      const campaignData = response?.data.map((el: CampaignData) => {
        let formattedStatus = "";
        switch (el.status) {
          case "draft": formattedStatus = "Draft"; break;
          case "testing": formattedStatus = "Testing"; break;
          case "completed": formattedStatus = "Complete"; break;
          case "scheduled": formattedStatus = "Scheduled"; break;
          case "in_progress": formattedStatus = "Live"; break;
          case "cancelled": formattedStatus = "Cancelled"; break;
          case "failed": formattedStatus = "Failed"; break;
          default: formattedStatus = el.status;
        }
        return { ...el, status: formattedStatus };
      }) || [];
      setPhishingArray(campaignData);
    } catch (error) {
      setPhishingArray([]);
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaignsData = async (value: any) => {
    setIsLoading(true);
    try {
      const response = await getPhishingCampaignData(value);
      setCampaignThresholdData(response.data);
      if (response.data?.is_threshold_reached) {
        setErrorMessage("Campaign limit reached. No more campaigns can be created.");
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
      } else {
        setErrorMessage("");
      }
    } catch (error: any) {
      setCampaignThresholdData(null);
      const errorMessage =
        error?.data?.errors[0] ||
        error?.data?.message ||
        "";
      if (!!errorMessage) {
        setErrorMessage(errorMessage);
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000)
      }
      console.error("Failed to fetch campaigns:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchTenants();
  }, []);


  useEffect(() => {
    if (!isSuperAdmin && TENANTS.length > 0 && !selectedTenantId) {
      setSelectedTenantId(TENANTS[0].value);
    }
  }, [TENANTS]);

  return (
    <Spin spinning={isLoading}>
      <div className="phishingManager">
        {!!showToast ?
          <Alert
            message={errorMessage ?? ""}
            type="warning"
            showIcon
            closable
            onClose={() => setShowToast(false)}
            icon={<Icon type="phishingManagerWarningModalPrefix" />}
            closeText={
              <div className="pm-icon-size">
                <Icon type="phishingManagerWarningModalClose" />
              </div>
            }
            className="campaign-limit-toast"
          /> : <></>}
        {newCampaignSetup ? <CampaignSetup tenantId={selectedTenantId} onBack={() => setNewCampaignSetup(false)} /> :
          <>  <div className="pm-header-row">
            <div className="pm-header-content">
              <div className="pm-title-group">
                <h3 className="pm-title">Phishing Manager</h3>
                <p className="pm-subtitle">Create, monitor, and learn from phishing simulations</p>
              </div>
              <div className="pm-actions">
                <label htmlFor="pm-client-select" className="pm-client-label">Tenant</label>
                <Select
                  id="pm-client-select"
                  dropdownClassName="cs-select-dropdown"
                  placeholder="Select a Tenant"
                  className="mw-150"
                  value={selectedTenantId}
                  loading={isLoading}
                  onChange={(value) => handleTenantChange(value)}
                  dropdownStyle={{ fontFamily: "Inter", fontSize: "16px", color: "#000" }}
                  showSearch
                  suffixIcon={<Icon type="campaignDropdown" />}
                  style={{ width: "100%", fontFamily: 'Inter' }}
                  options={TENANTS}
                />

                <button
                  type="button"
                  className={`pm-new-campaign-btn ${!selectedTenantId
                    ? "pm-new-campaign-btn-toggle"
                    : !!errorMessage
                      ? "pm-new-campaign-btn-disable-toggle"
                      : ""
                    }`}
                  onClick={handleCreateCampaign}
                  disabled={
                    !selectedTenantId
                  }
                  aria-label="Create new campaign"
                >
                  <Icon type="campaignAdd" />
                  <span className="pm-new-campaign-text">New Campaign</span>
                </button>
              </div>
            </div>
          </div>

            {
              phishingArray.length === 0 ? !selectedTenantId ? <div className="pm-empty-card">
                <div>
                  <Icon type="noCampaign" />

                  <div className="pm-empty-title">No tenants selected yet</div>
                </div>
                <div className="pm-empty-description">
                  You haven’t selected any tenants campaigns yet. Start by selecting a tenant
                  to create a phishing campaign.
                </div>
              </div>
                : (
                  <>
                    <div className="pm-empty-card">
                      <div>
                        <Icon type="noCampaign" />

                        <div className="pm-empty-title">No campaigns yet</div>
                      </div>

                      <div className="pm-empty-description">
                        You haven’t created any phishing campaigns yet. Start by creating a campaign
                        to simulate phishing attacks and measure how your users respond.
                      </div>

                      <div className="pm-empty-button-wrapper">
                        <button className="pm-empty-button" onClick={handleCreateCampaign}>Create Campaign</button>
                      </div>
                    </div>
                  </>
                ) : (
                <>
                  <div className="pm-campaigns-header">Campaigns</div>
                  <div className="pm-campaigns-card">
                    {phishingArray.map((item) => (
                      <div className="pm-card-row" key={item.id}>
                        <div className="pm-card-icon">
                          <img width="80" height="80" alt={item.name} src={item.image_url} />
                        </div>
                        <div className="campaign-card">
                          <div className="pm-campaign-card">{item.name}</div>
                          <div className="campaign-card-wrapper" style={{ background: statusStyles[item.status]?.background ?? 'transparent' }}>
                            <span className="campaign-card-status" style={{ color: statusStyles[item.status]?.color ?? '#000' }}>{item.status}</span>
                          </div>
                          <div className="pm-row-stretch">
                            <Icon type="campaignCalendar" />
                            <span className="pm-card-date">
                              {`${moment(item.schedule_datetime).format("M/D/YYYY")} - ${moment(
                                item.schedule_datetime
                              )
                                .add(Number(item.campaign_duration), "days")
                                .format("M/D/YYYY")}`}
                            </span>
                          </div>
                        </div>

                        <Dropdown
                          placement="bottomRight"
                          overlayClassName="pm-custom-dropdown-overlay"
                          overlay={
                            <Menu onClick={handleMenuClick(item.id.toString())} className="pm-custom-dropdown-menu">
                              <Menu.Item key="send" className="pm-custom-menu-item">Send full campaign</Menu.Item>
                              <Menu.Item key="rerun" className="pm-custom-menu-item">Re-run test</Menu.Item>
                              <Menu.Item key="view" className="pm-custom-menu-item">View setup summary</Menu.Item>
                              <Menu.Item key="discard" className="pm-custom-menu-item">Discard campaign</Menu.Item>
                            </Menu>
                          }
                          trigger={['click']}
                        >
                          <div className="pm-icon-button">
                            <div className="pm-icon">⋯</div>
                          </div>
                        </Dropdown>
                      </div>
                    ))}
                  </div>
                    <div className="pm-pagination-wrapper">
                      <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={pageSize}
                        onChange={(page) => {
                          setCurrentPage(page);
                          fetchCampaigns(selectedTenantId, page);
                        }}
                        showSizeChanger={false}
                        hideOnSinglePage={true}
                        responsive={true}
                      />
                    </div>
                </>
              )
            }
            < Modal
              visible={isModalVisible}
              onCancel={closeModal}
              footer={null}
              closable={false}
              centered
              width={495}
              bodyStyle={{ padding: 0 }}
            >
              <div className="pm-modal-container">
                <div className="pm-modal-header">
                  <div className="pm-modal-header-left">
                    <span className="pm-modal-title">Discard campaign</span>
                  </div>
                  <button className="pm-modal-close-icon" onClick={closeModal} aria-label="Close">
                    <Icon type="campaignModalClose" />
                  </button>
                </div>

                <div className="pm-modal-body">
                  <p>This will permanently remove this campaign and stop any ongoing activity.</p>
                  <br />
                  <p>You will not be able to recover it.</p>
                </div>

                <div className="pm-modal-footer">
                  <div className="pm-modal-footer-actions">
                    <button className="pm-btn-plain" onClick={closeModal}>
                      Keep campaign
                    </button>
                    <button className="pm-btn-primary" onClick={confirmDiscard}>
                      Discard campaign
                    </button>
                  </div>
                </div>
              </div>
            </Modal >
            <Modal
              visible={warningModal}
              onCancel={closeWarningModal}
              footer={null}
              closable={false}
              centered
              width={495}
              bodyStyle={{ padding: 0 }}
            >
              <div className="pmw-modal-container">
                <div className="pm-modal-header">
                  <div className="pm-modal-header-left">
                    <span className="pm-modal-title"></span>
                  </div>
                  <button className="pm-modal-close-icon" onClick={closeWarningModal} aria-label="Close">
                    <Icon type="campaignModalClose" />
                  </button>
                </div>
                <div className="pm-modal-body">
                  <div className="pm-icon-box" >
                    <div className="pm-icon-wrapper">
                      <div className="pm-icon-warning-modal">
                        <Icon type="phishingManagerWarningModal" />
                      </div>
                    </div>
                  </div>
                  <p className="pm-modal-title-text">
                    You’re approaching your campaign limit
                  </p>

                  <p> You are about to create your last available campaign under your current plan.</p>
                  <br />
                  <p>
                    Once this campaign is created, you won’t be able to create additional campaigns.</p>
                  <br />
                  <p>
                    To create more campaigns, please contact Customer Success to see available options.</p>
                </div>

                <div className="pm-modal-footer">
                  <div className="pm-modal-footer-actions">
                    <button className="pm-btn-plain" onClick={closeWarningModal}>
                      Cancel
                    </button>
                    <button className="pm-btn-primary" onClick={confirmWarningDiscard}>
                      Create campaign
                    </button>
                  </div>
                </div>
              </div>
            </Modal>

            <div className="pm-info-banner">
              <div className="pm-info-content">
                <div className="pm-info-row">
                  <div className="pm-info-icon-wrapper">
                    <div className="pm-info-icon">
                      <Icon type="campaignInfo" />
                    </div>
                  </div>

                  <div className="pm-info-text-group">
                    <div className="pm-info-title">
                      Configure Whitelisting for Successful Campaigns
                    </div>
                    <div className="pm-info-description">
                      To ensure phishing simulations are delivered, clients must whitelist
                      Venza’s email infrastructure. This step allows campaigns to be
                      accessible and prevents client-side security controls from blocking
                      email delivery.
                    </div>
                  </div>
                </div>
              </div>

              <div className="pm-info-action">
                <a
                  href="https://www.venzapeak.com/support/public_resources/phishing/whitelist/#/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pm-info-action-text"
                >
                  View Whitelisting Guide
                </a>
              </div>

            </div>
          </>
        }

      </div >
    </Spin>
  );
};

export default PhishingManager;